import { useCallback } from 'react';

import {
  GroupedSelectOption,
  LabelProps,
  LoadingBubbles,
  Select,
  SelectOption,
} from '@scaleaq/scaleui';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { styled } from 'styled-components';

const FieldWrapper = styled.div<{ $maxWidth?: string }>`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
  span {
    flex: 1;
  }

  > div {
    flex: 1;
    width: 100%;
  }
`;

type ControlledSelectFieldProps<T extends FieldValues, V> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  caution?: boolean | string;
  critical?: boolean | string;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;

  render?: boolean;
  placeholder: string;
  options: Array<SelectOption<V> | GroupedSelectOption<SelectOption<V>>>;
  translations?: {
    noOptions: string;
  };
  maxWidth?: string;
  onSelect?: (value?: SelectOption<V>) => void;
  isLoading?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
} & LabelProps;

export const ControlledSelectField = <T extends FieldValues, V>({
  label,
  name,
  control,
  options,
  caution,
  critical,
  rules,
  render = true,
  placeholder,
  translations,
  maxWidth,
  onSelect,
  isLoading = false,
  ...rest
}: ControlledSelectFieldProps<T, V>) => {
  const getSelectedOption = useCallback(
    (value: V) => {
      if (value === undefined || value === null) return null;
      const nestedOptions = options.flatMap(
        o => ((o as GroupedSelectOption<SelectOption<V>>).options ?? [o]) as SelectOption<V>[]
      );
      return nestedOptions.find(o => o.value === value);
    },
    [options]
  );

  if (!render) return;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value } }) => {
        return (
          <FieldWrapper $maxWidth={maxWidth}>
            <Select
              {...rest}
              label={label}
              placeholder={placeholder}
              options={options}
              translations={translations}
              onChange={selectedOption => {
                onChange(selectedOption?.value);
                onSelect?.(selectedOption as SelectOption<V>);
              }}
              value={getSelectedOption(value)}
              caution={caution}
              critical={critical}
              isLoading={isLoading}
              style={{
                color: 'var(--sui-text-neutral-100)',
                fontFamily: 'var(--sui-font-family-body)',
                fontWeight: 'var(--sui-font-weight-regular)',
                fontSize: 'var(--sui-font-size-medium)',
              }}
              components={{ LoadingIndicator: () => <LoadingBubbles /> }}
            />
          </FieldWrapper>
        );
      }}
    />
  );
};
